import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { useGetPendingApprovalsQuery } from "@/services/(company)/attendanceRegularizationApi";
import { useGetCompanyTimeAttendanceStatsQuery } from "@/services/(company)/api.time-attendance";
import { useGetLeaveRequestStatQuery } from "@/services/(company)/leaveRequestApi";
import { PATHS } from "@/routes/paths";
import { formatDistanceToNow } from "date-fns";
import { useLocale } from "@/hooks/useLocale";
import {
  AvatarStack,
  HeroLine,
  MetricValue,
  MiniMetric,
  MiniStat,
  PersonRow,
  RowsSkeleton,
  WidgetEmpty,
  WidgetNoAccess,
} from "./primitives";

const buildTypeLabel = (t: TFunction) =>
  ({
    LEAVE_REQUEST: { label: t("company:dashboard.leave"), statusKey: "leave" },
    REGULARIZATION: {
      label: t("company:dashboard.regularization"),
      statusKey: "overtime",
    },
  }) as const;

/** Rows each list size shows without clipping. `grand` is `xl`'s width with a third row of height. */
const LIST_ROWS = { lg: 3, xl: 4, grand: 8 } as const;

/**
 * Everything waiting on an approver, leave and regularization together.
 *
 * The compact sizes are a count; the tall sizes are a worklist, because a
 * pending-approvals number the user cannot act on is a dead end.
 */
export const PendingApprovalsWidget = ({
  size,
  isEditing,
}: IWidgetRenderContext) => {
  const { t } = useTranslation("company");
  const { formatDate, locale } = useLocale();
  const TYPE_LABEL = buildTypeLabel(t);
  // `addSuffix: false`, not `useLocale().formatRelative`: "waiting" already
  // supplies the direction, so a helper that appends "ago" would read
  // "waiting 3 days ago".
  const formatWaitingDuration = (date: string | Date) =>
    formatDistanceToNow(new Date(date), {
      addSuffix: false,
      locale: locale.dateFnsLocale,
    });
  // The list is a page of the eight most recent (`grand` shows up to eight
  // rows, and a cap below what the tallest layout wants would silently
  // under-fill it); the counts come from the stats endpoints, which are the
  // only source that knows the real totals. Counting the array instead — as
  // this widget used to — capped the headline at the page size, so a company
  // with forty pending approvals reported eight.
  const { data, isLoading, error } = useGetPendingApprovalsQuery({ limit: 8 });
  const {
    data: leaveStats,
    isLoading: isLeaveStatsLoading,
    error: leaveError,
  } = useGetLeaveRequestStatQuery();
  const {
    data: attendanceStats,
    isLoading: isAttendanceStatsLoading,
    error: attendanceError,
  } = useGetCompanyTimeAttendanceStatsQuery({});

  // The worklist is this widget's own gate. Without it there is nothing to act
  // on, which is the entire point of the widget.
  if (error) return <WidgetNoAccess error={error} />;

  // The headline sums two modules the registry gated this widget on neither of
  // individually — one of them (time-attendance) it happens to share with the
  // worklist's own guard above, the other (leave) is genuinely foreign. Either
  // can fail on its own: a Time & Attendance manager with no leave access sees
  // only the regularization half, and that half is still real and still
  // actionable. So each half zeroes out independently on its own failure —
  // understating the total is the same safe direction already used below for
  // "oldest" — and the figure is only a dash when BOTH are unreadable, since at
  // that point there is truly nothing behind it.
  const noLeaveCount = Boolean(leaveError);
  const noRegularizationCount = Boolean(attendanceError);
  const noTotal = noLeaveCount && noRegularizationCount;
  const isStatsLoading = isLeaveStatsLoading || isAttendanceStatsLoading;

  const items = data?.data ?? [];
  const leaveCount = leaveStats?.data?.pendingLeaveRequests ?? 0;
  const regularizationCount =
    attendanceStats?.data?.tabCounts?.regularizationRequestCount ?? 0;
  const total = leaveCount + regularizationCount;

  // One entry per person, not per request: someone with a leave request and a
  // regularization is one face, and a stack that showed them twice would be
  // read as two people waiting.
  const requesters = Array.from(
    new Map(
      items.map((item) => [
        item.userId,
        { id: item.userId, name: item.fullName, image: item.profileImage },
      ]),
    ).values(),
  );

  // The head of the queue: whoever has been waiting longest. The endpoint
  // returns most-recent-first, so the oldest of the loaded page is its last
  // entry — a floor on the true wait, not the exact one, since an older request
  // could sit beyond the page. Understating "waiting since" is the safe
  // direction: it can only ever read as less urgent than reality, never more.
  const oldest = items.length > 0 ? items[items.length - 1] : undefined;

  const detailPath = (item: (typeof items)[number]) =>
    item.type === "LEAVE_REQUEST"
      ? PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.LEAVES_SUMMARY(item.id)
      : PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.VIEW_REGULARIZATION(
          item.id,
        );

  // The card draws the chip and the title at every size, this one included, so
  // there is nothing here to add beyond the number.
  if (size === "xs") {
    return (
      <MiniMetric
        value={total}
        isLoading={isLoading || isStatsLoading}
        noAccess={noTotal}
        tone="warning"
      />
    );
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-0.5">
        <MetricValue
          value={total}
          isLoading={isLoading || isStatsLoading}
          noAccess={noTotal}
          tone="warning"
        />
        <p className="text-[11px] text-muted-foreground">
          {noTotal
            ? t("company:widgets.count_unavailable")
            : t("company:widgets.request_needs_action", { count: total })}
        </p>
      </div>
    );
  }

  // Leads with who is waiting and for how long, not with how many are. The
  // count moves to the right of the name as context — an approver opening this
  // wants the next thing to act on, and "8" is not that.
  //
  // No leave/regularization breakdown here: `md` is one row tall, and that
  // row is already spent on the queue head and the total. The breakdown
  // reappears from `lg` up, where a second row actually exists to hold it.
  if (size === "md") {
    return (
      <div className="flex h-full items-center">
        {!isLoading && !oldest ? (
          <WidgetEmpty
            message={t("company:widgets.nothing_waiting_on_approver")}
          />
        ) : (
          <HeroLine
            isLoading={isLoading}
            avatar={
              oldest && {
                name: oldest.fullName,
                image: oldest.profileImage,
              }
            }
            primary={oldest?.fullName ?? ""}
            caption={
              oldest
                ? t("company:widgets.type_waiting_duration", {
                    type: TYPE_LABEL[oldest.type].label,
                    duration: formatWaitingDuration(oldest.createdAt),
                  })
                : undefined
            }
            trailing={
              <MetricValue
                value={total}
                isLoading={isLoading || isStatsLoading}
                noAccess={noTotal}
                tone="warning"
                scale="md"
              />
            }
          />
        )}
      </div>
    );
  }

  // `size` is typed against every size in the system; this widget offers three
  // of the seven, so the map has no entry for the rest.
  const rows = LIST_ROWS[size as keyof typeof LIST_ROWS] ?? LIST_ROWS.lg;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-3">
        <MetricValue
          value={total}
          isLoading={isLoading || isStatsLoading}
          noAccess={noTotal}
          tone="warning"
          scale="md"
        />
        <AvatarStack
          people={requesters}
          max={4}
          showOverflowLabel={false}
          size="sm"
          isLoading={isLoading}
        />
        <div className="grid flex-1 grid-cols-2 gap-2">
          <MiniStat
            label={TYPE_LABEL.LEAVE_REQUEST.label}
            tone="info"
            value={leaveCount}
            isLoading={isLeaveStatsLoading}
            noAccess={noLeaveCount}
          />
          <MiniStat
            label={TYPE_LABEL.REGULARIZATION.label}
            tone="warning"
            value={regularizationCount}
            isLoading={isAttendanceStatsLoading}
            noAccess={noRegularizationCount}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden border-t border-border pt-1.5">
        {isLoading ? (
          <RowsSkeleton rows={rows} />
        ) : items.length === 0 ? (
          <WidgetEmpty
            message={t("company:widgets.nothing_waiting_on_approver")}
          />
        ) : (
          <ul className="flex flex-col">
            {items.slice(0, rows).map((item) => (
              <li key={`${item.type}-${item.id}`}>
                <PersonRow
                  name={item.fullName}
                  image={item.profileImage}
                  primary={item.fullName}
                  secondary={
                    item.startDate
                      ? formatDate(new Date(item.startDate), "dd MMM yyyy")
                      : item.requestDate
                        ? formatDate(new Date(item.requestDate), "dd MMM yyyy")
                        : item.summary
                  }
                  to={detailPath(item)}
                  isEditing={isEditing}
                  trailing={
                    <Badge
                      variant={statusToVariant(TYPE_LABEL[item.type].statusKey)}
                      dot={false}
                      className="shrink-0"
                    >
                      {TYPE_LABEL[item.type].label}
                    </Badge>
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
